import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { authService } from '../firebase/services';

const BulkDeleteAuthUsers = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [missingUsers, setMissingUsers] = useState({ email: [], phone: [] });
  const [showUserList, setShowUserList] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState({ email: [], phone: [] });
  const [deletingUsers, setDeletingUsers] = useState([]);
  const [activeTab, setActiveTab] = useState('phone'); // 'phone' or 'email'

  const handleStartCleanup = async () => {
    setIsLoading(true);
    setError(null);
    setProgress(0);

    try {
      const result = await authService.findMissingAuthUsers((progress) => {
        setProgress(progress);
      });

      setMissingUsers(result.missingUsers);
      setShowUserList(true);
    } catch (err) {
      setError(err.message || 'An error occurred while finding missing users');
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmBulkDelete = () => {
    setShowConfirmation(true);
  };

  const handleConfirmCleanup = async () => {
    setShowConfirmation(false);
    setIsLoading(true);
    setProgress(0);
    setError(null);
    setSummary(null);

    try {
      const allSelectedUsers = [...selectedUsers.phone, ...selectedUsers.email];
      const result = await authService.bulkDeleteMissingAuthUsers(allSelectedUsers, (progress) => {
        setProgress(progress);
      });

      setSummary(result);
      setShowUserList(false);
      setMissingUsers({ email: [], phone: [] });
      setSelectedUsers({ email: [], phone: [] });
    } catch (err) {
      setError(err.message || 'An error occurred during cleanup');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelCleanup = () => {
    setShowConfirmation(false);
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleSelectAll = (type) => {
    const currentUsers = missingUsers[type];
    const currentSelected = selectedUsers[type];
    
    if (currentSelected.length === currentUsers.length) {
      setSelectedUsers(prev => ({
        ...prev,
        [type]: []
      }));
    } else {
      setSelectedUsers(prev => ({
        ...prev,
        [type]: currentUsers.map(user => user.uid)
      }));
    }
  };

  const handleSelectUser = (uid, type) => {
    setSelectedUsers(prev => ({
      ...prev,
      [type]: prev[type].includes(uid) 
        ? prev[type].filter(id => id !== uid)
        : [...prev[type], uid]
    }));
  };

  const handleDeleteIndividual = async (uid, type) => {
    setDeletingUsers(prev => [...prev, uid]);
    
    try {
      await authService.deleteSingleAuthUser(uid);
      setMissingUsers(prev => ({
        ...prev,
        [type]: prev[type].filter(user => user.uid !== uid)
      }));
      setSelectedUsers(prev => ({
        ...prev,
        [type]: prev[type].filter(id => id !== uid)
      }));
    } catch (err) {
      setError(`Failed to delete user ${uid}: ${err.message}`);
    } finally {
      setDeletingUsers(prev => prev.filter(id => id !== uid));
    }
  };

  const handleReset = () => {
    setShowUserList(false);
    setMissingUsers({ email: [], phone: [] });
    setSelectedUsers({ email: [], phone: [] });
    setSummary(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              <h1 className="text-2xl font-semibold text-gray-900">User Management</h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Main Card */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Delete Auth Users Not in Users Collection
            </h2>
            
            <div className="text-gray-600 mb-6">
              <p className="mb-3">
                This tool will compare Firebase Auth users with the users collection by UID, then identify Auth users who don't have corresponding documents in Firestore.
              </p>
              <p className="mb-3">
                <strong>Comparison Logic:</strong> Auth users whose UIDs are not found in the users collection will be marked for deletion.
              </p>
              <p className="text-sm text-gray-500">
                This action cannot be undone. Please ensure you have a backup before proceeding.
              </p>
            </div>

            {/* Action Button */}
            {!isLoading && !summary && !showUserList && (
              <button
                onClick={handleStartCleanup}
                className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                Find Missing Auth Users
              </button>
            )}

            {/* Progress Indicator */}
            {isLoading && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Processing...</span>
                  <span className="text-sm text-gray-500">{Math.round(progress)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-500">
                  Please do not close this page during the process.
                </p>
              </div>
            )}

            {/* User List */}
            {showUserList && (missingUsers.phone.length > 0 || missingUsers.email.length > 0) && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Missing Auth Users
                  </h3>
                  <div className="flex space-x-2">
                    <button
                      onClick={handleReset}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Reset
                    </button>
                  </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                  <nav className="-mb-px flex space-x-8">
                    <button
                      onClick={() => setActiveTab('phone')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'phone'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Phone Auth Users ({missingUsers.phone.length})
                    </button>
                    <button
                      onClick={() => setActiveTab('email')}
                      className={`py-2 px-1 border-b-2 font-medium text-sm ${
                        activeTab === 'email'
                          ? 'border-blue-500 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      Email Auth Users ({missingUsers.email.length})
                    </button>
                  </nav>
                </div>

                {/* Tab Content */}
                <div className="space-y-4">
                  {/* Phone Users Tab */}
                  {activeTab === 'phone' && (
                    <div>
                      {missingUsers.phone.length > 0 ? (
                        <>
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-sm text-gray-600">
                              Phone-based authentication users not in users collection
                            </span>
                            <button
                              onClick={() => handleSelectAll('phone')}
                              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              {selectedUsers.phone.length === missingUsers.phone.length ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                            <div className="space-y-2">
                              {missingUsers.phone.map((user) => (
                                <div 
                                  key={user.uid} 
                                  className="flex items-center justify-between p-3 bg-white rounded-md border"
                                >
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedUsers.phone.includes(user.uid)}
                                      onChange={() => handleSelectUser(user.uid, 'phone')}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div>
                                      <p className="font-medium text-gray-900">{user.phone || 'No phone'}</p>
                                      <p className="text-sm text-gray-500">UID: {user.uid}</p>
                                      {user.displayName && (
                                        <p className="text-sm text-gray-500">Name: {user.displayName}</p>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteIndividual(user.uid, 'phone')}
                                    disabled={deletingUsers.includes(user.uid)}
                                    className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {deletingUsers.includes(user.uid) ? 'Deleting...' : 'Delete'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          No phone-based missing users found
                        </div>
                      )}
                    </div>
                  )}

                  {/* Email Users Tab */}
                  {activeTab === 'email' && (
                    <div>
                      {missingUsers.email.length > 0 ? (
                        <>
                          <div className="flex items-center justify-between mb-4">
                            <span className="text-sm text-gray-600">
                              Email-based authentication users not in users collection
                            </span>
                            <button
                              onClick={() => handleSelectAll('email')}
                              className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                              {selectedUsers.email.length === missingUsers.email.length ? 'Deselect All' : 'Select All'}
                            </button>
                          </div>

                          <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
                            <div className="space-y-2">
                              {missingUsers.email.map((user) => (
                                <div 
                                  key={user.uid} 
                                  className="flex items-center justify-between p-3 bg-white rounded-md border"
                                >
                                  <div className="flex items-center space-x-3">
                                    <input
                                      type="checkbox"
                                      checked={selectedUsers.email.includes(user.uid)}
                                      onChange={() => handleSelectUser(user.uid, 'email')}
                                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <div>
                                      <p className="font-medium text-gray-900">{user.email || 'No email'}</p>
                                      <p className="text-sm text-gray-500">UID: {user.uid}</p>
                                      {user.displayName && (
                                        <p className="text-sm text-gray-500">Name: {user.displayName}</p>
                                      )}
                                    </div>
                                  </div>
                                  <button
                                    onClick={() => handleDeleteIndividual(user.uid, 'email')}
                                    disabled={deletingUsers.includes(user.uid)}
                                    className="px-3 py-1 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {deletingUsers.includes(user.uid) ? 'Deleting...' : 'Delete'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          No email-based missing users found
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Bulk Delete Button */}
                {(selectedUsers.phone.length > 0 || selectedUsers.email.length > 0) && (
                  <div className="flex items-center justify-between p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                    <span className="text-yellow-800">
                      {selectedUsers.phone.length + selectedUsers.email.length} user(s) selected for bulk deletion
                    </span>
                    <button
                      onClick={handleConfirmBulkDelete}
                      className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                    >
                      Delete Selected ({selectedUsers.phone.length + selectedUsers.email.length})
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* No Missing Users */}
            {showUserList && missingUsers.phone.length === 0 && missingUsers.email.length === 0 && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <h3 className="text-lg font-medium text-green-800 mb-2">No Missing Users Found</h3>
                <p className="text-green-700">
                  All Firebase Auth users have corresponding documents in the users collection.
                </p>
                <button
                  onClick={handleReset}
                  className="mt-3 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                >
                  Check Again
                </button>
              </div>
            )}

            {/* Summary */}
            {summary && (
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <h3 className="text-lg font-medium text-green-800 mb-3">Cleanup Complete</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-green-700">Total Auth Users:</span>
                    <span className="font-medium">{summary.totalAuthUsers}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Total Users Collection Documents:</span>
                    <span className="font-medium">{summary.totalUsersCollection}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-green-700">Deleted Users:</span>
                    <span className="font-medium text-red-600">{summary.deletedUsers}</span>
                  </div>
                </div>
                <button
                  onClick={handleBack}
                  className="mt-4 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
                >
                  Return to Dashboard
                </button>
              </div>
            )}

            {/* Error Display */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <h3 className="text-lg font-medium text-red-800 mb-2">Error</h3>
                <p className="text-red-700">{error}</p>
                <button
                  onClick={() => setError(null)}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                >
                  Try Again
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Modal */}
        {showConfirmation && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Confirm Cleanup
                </h3>
                <p className="text-sm text-gray-600 mb-6">
                  Are you sure? This will permanently delete all Auth users not present in the users collection.
                </p>
                <div className="flex space-x-3">
                  <button
                    onClick={handleConfirmCleanup}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                  >
                    Confirm Delete
                  </button>
                  <button
                    onClick={handleCancelCleanup}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkDeleteAuthUsers; 